import styles from './n25-search-bar.module.css';
import Clear from '$icons/close.svg';
import Search from '$icons/solidAsh/search.svg';
import { debounce, useTranslation } from '~/shared/utils';
import { useSearchHook, useSearchRouting } from '~/features/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { NavigationNode } from '~/lib/data-contract/auto-generated/api-model';
import { CMSLink, Typography } from '~/shared/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

type Props = {
    isIconRight?: boolean;
    onFocus?: () => void;
    className?: string;
    selectedSubMenu?: NavigationNode | null;
    searchUrl?: string;
    isSearchPage?: boolean;
};

const N25SearchBar = ({
    isIconRight,
    onFocus,
    className,
    selectedSubMenu,
    searchUrl,
    isSearchPage,
}: Props) => {
    const { translate } = useTranslation();
    const { searchPageRouteConnector, searchQuery } = useSearchRouting();
    const { clearSearch } = useSearchHook();

    const ref = useRef<HTMLInputElement | null>(null);

    const onSearchClear = useCallback(() => {
        if (ref.current) {
            ref.current.value = '';
        }
        clearSearch();
        searchPageRouteConnector('');
    }, [clearSearch, searchPageRouteConnector]);

    useEffect(() => {
        const input = ref.current;
        if (!input) return;

        if (document.activeElement === input) return;

        if (input.value !== searchQuery) {
            input.value = searchQuery;
        }
    }, [searchQuery]);

    const debouncedSearch = debounce(
        (searchTerm: string) => searchPageRouteConnector(searchTerm),
        500
    );

    return (
        <>
            <div
                className={clsx(styles.root, className, {
                    [styles.selectedSubMenu]: selectedSubMenu,
                })}
            >
                <CMSLink href={searchUrl} className={styles.link} onClick={onFocus}>
                    <VisuallyHidden>{translate('header.search.placeholder.mobile')}</VisuallyHidden>
                </CMSLink>
                <input
                    type="search"
                    id="headerSearchField"
                    ref={ref}
                    defaultValue={searchQuery}
                    onInput={(event) => debouncedSearch(event.currentTarget.value)}
                    aria-label={translate('header.search.placeholder')}
                    className={clsx(styles.input, {
                        [styles.iconRight]: isIconRight,
                    })}
                    placeholder={translate('header.search.placeholder')}
                    onFocus={onFocus}
                />

                {searchQuery ? (
                    <Clear
                        aria-hidden="true"
                        onClick={onSearchClear}
                        className={clsx(styles.icon, { [styles.iconRight]: isIconRight })}
                    />
                ) : (
                    <Search
                        aria-hidden="true"
                        className={clsx(styles.icon, { [styles.iconRight]: isIconRight })}
                    />
                )}
            </div>
            <Typography
                as="span"
                className={clsx(styles.subMenuTitle, {
                    [styles.selectedSubmenu]: selectedSubMenu,
                    [styles.searchPage]: isSearchPage,
                })}
            >
                {selectedSubMenu?.link?.text}
            </Typography>
        </>
    );
};

export default N25SearchBar;
